import * as React from "react";
import { useState, useEffect } from "react";
import "rsuite/dist/rsuite.css";
import { Helmet } from "react-helmet-async";
import TablePaginationActions from "../../common/components/pagination.actions";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  TablePagination,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  useTheme,
  TableCell,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
} from "@mui/material";
import ProgressLoader from "../../common/components/progress.loader";
import {
  useGetAllWebhookConfigurationsQuery,
  useUpdateWebhookStatusMutation,
  useDeleteWebhookConfigurationsMutation,
} from "../../services/API/webHookApiSlice";
import { UpdateSubscriberStatusRequest } from "../../types/WebHookTypes";
import { toast } from "react-toastify";
import EnhancedTableHead from "../../common/components/enhancedtable/enhancedtablehead";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import { useNavigate, Navigate } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ContentCopy from "@mui/icons-material/ContentCopy";
import { copyText } from "./webhook.common";
import { useFlags } from "launchdarkly-react-client-sdk";

const ManageWebHooks = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [isStatusModalOpen, setStatusModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // filters
  const [searchText, setSearchText] = useState("");
  const [subscriberList, setSubscriberList] = React.useState<any>([]);
  const [sortField, setSortField] = React.useState<any>("CreatedDate");
  const [sortOrder, setSortOrder] = React.useState<any>("desc");
  // Menues
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  // Get Launch Darkly Flags
  const flags = useFlags();
  const allowWebhookIntegration = flags.allowWebhookIntegrations;

  const [updateWebhookStatus] = useUpdateWebhookStatusMutation();
  const [deleteWebhook] = useDeleteWebhookConfigurationsMutation();

  const columns = [
    {
      label: "ID",
      accessor: "id",
      sortable: false,
      display: "table-cell",
    },
    {
      label: "Merchant",
      accessor: "merchantId",
      sortable: true,
      display: "table-cell",
    },
    {
      label: "Endpoint URL",
      accessor: "endpointUrl",
      sortable: true,
      display: "table-cell",
    },
    {
      label: "Subscribed Events",
      accessor: "eventNames",
      sortable: true,
      display: "table-cell",
    },
    {
      label: "Status",
      accessor: "isActive",
      sortable: true,
      display: "table-cell",
    },
    {
      label: "Action",
      accessor: "",
      sortable: false,
      display: "table-cell",
    },
  ];

  const { data, isFetching } = useGetAllWebhookConfigurationsQuery(
    {
      page: page + 1,
      pageSize: rowsPerPage,
      sortField: sortField,
      sortDirection: sortOrder,
      searchText: searchText,
    },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (data) {
      if (data.length === 0 && !isFetching) toast.warning("No Record found!");

      setSubscriberList(data);
    }
  }, [data, isFetching]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleSorting = (sortField, sortOrder) => {
    setSortField(sortField);
    setSortOrder(sortOrder);
  };

  const handleOpen = (event, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const closeModal = () => {
    setSelectedRow(null);
    setStatusModalOpen(false);
    setDeleteModalOpen(false);
  };

  const performStatusChange = async () => {
    if (selectedRow) {
      try {
        const payload: UpdateSubscriberStatusRequest = {
          id: selectedRow?.id,
          status: !selectedRow?.isActive,
        };

        setIsProcessing(true);
        const response = await updateWebhookStatus(payload).unwrap();
        if (response !== undefined && response.id)
          toast.success("Status changed Successfully!");
        else toast.error("An unexpected error occurred.");
      } catch (err) {
        toast.error(err);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const performDeletion = async () => {
    if (selectedRow) {
      try {
        setIsProcessing(true);
        const response = await deleteWebhook(selectedRow?.id).unwrap();
        if (response !== undefined && response.id)
          toast.success("Webhook Deleted Successfully!");
        else toast.error("An unexpected error occurred.");
      } catch (err) {
        toast.error(err);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const onEdit = () => {
    navigate("../modifyWebHook", {
      state: { id: selectedRow?.id },
    });
  };

  return (
    <>
    {allowWebhookIntegration === false && <Navigate to="/401" />}
      <Helmet>
        <title> Manage Webhooks | {theme.browserTitle} </title>
      </Helmet>
      <Box>
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography
              variant="h6"
              gutterBottom
              style={{ fontWeight: "bold" }}
            >
              Manage Webhooks
              {isProcessing && (
                <CircularProgress
                  size={25}
                  style={{ marginLeft: "20px", float: "right" }}
                  color="info"
                />
              )}
            </Typography>
            <Stack direction="row" alignItems="center">
              <TextField
                id="outlined-search"
                placeholder="Search"
                sx={{ width: "300px" }}
                size="small"
                value={searchText}
                onChange={handleSearchChange}
                //onKeyDown={handleSearchKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Stack>
        </Box>
        <Box component={Paper}>
          {isFetching ? (
            <>
              <TableContainer sx={{ height: "80vh" }}>
                <Table size="small" className="sticky-header sortable">
                  <EnhancedTableHead
                    columns={columns}
                    handleSorting={() => {}}
                    currentSortField=""
                    currentSortOrder=""
                  />
                </Table>
              </TableContainer>
              <Box
                sx={{
                  position: "absolute",
                  zIndex: "5",
                  top: "30%",
                  left: "50%",
                  height: "68vh",
                }}
              >
                <ProgressLoader isFetching={isFetching}></ProgressLoader>
              </Box>
            </>
          ) : (
            <Box>
              <TableContainer
                sx={{ height: "76vh" }}
                style={{ overflow: "auto", scrollbarWidth: "thin" }}
              >
                <Table size="small" className="sticky-header sortable">
                  <EnhancedTableHead
                    columns={columns}
                    handleSorting={handleSorting}
                    currentSortField={sortField}
                    currentSortOrder={sortOrder}
                  />
                  <TableBody>
                    {!subscriberList || subscriberList?.data?.length == 0 ? (
                      <TableRow>
                        <TableCell colSpan={9}>No record found.</TableCell>
                      </TableRow>
                    ) : (
                      subscriberList?.data?.map((row: any, index) => (
                        <TableRow key={index}>
                          <TableCell align="left">{row.id}</TableCell>
                          <TableCell align="left">{row.merchantId}</TableCell>
                          <TableCell align="left" sx={{
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-all", // breaks long continuous strings
                                                        maxWidth: 400,          // adjust column width
                                                      }}>
                            {row.endpointUrl}
                            <IconButton
                              onClick={() => copyText(row.endpointUrl)}
                              edge="end"
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </TableCell>
                          <TableCell align="left">
                            {row.eventGroupName ?? row.eventNames?.[0]}
                          </TableCell>
                          <TableCell align="left">
                            <Chip
                              icon={
                                row.isActive ? (
                                  <CheckCircleIcon style={{ color: "green" }} />
                                ) : (
                                  <RadioButtonUncheckedIcon />
                                )
                              }
                              label={row.isActive ? "Enabled" : "Disabled"}
                              variant="outlined"
                              style={{
                                color: row.isActive ? "green" : "gray",
                                borderColor: row.isActive ? "green" : "gray",
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <IconButton
                              aria-label="more"
                              aria-controls={open ? "action-menu" : undefined}
                              aria-haspopup="true"
                              onClick={(e) => handleOpen(e, row)}
                            >
                              <MoreVertIcon />
                            </IconButton>

                            <Menu
                              id={"action-menu" + index}
                              anchorEl={anchorEl}
                              open={open && selectedRow?.id === row.id}
                              onClose={handleClose}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                            >
                              <MenuItem
                                onClick={() => {
                                  onEdit();
                                  handleClose();
                                }}
                              >
                                <ListItemIcon>
                                  <EditIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Edit Configuration</ListItemText>
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  setStatusModalOpen(true);
                                  handleClose();
                                }}
                              >
                                <ListItemIcon>
                                  <SwapHorizIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>
                                  {selectedRow?.isActive
                                    ? "Disable Webhook"
                                    : "Enable Webhook"}
                                </ListItemText>
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  setDeleteModalOpen(true);
                                  handleClose();
                                }}
                              >
                                <ListItemIcon>
                                  <DeleteIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Delete Webhook</ListItemText>
                              </MenuItem>
                            </Menu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="end"
                style={{ marginTop: "5px", borderTop: "1px solid #c8c8c8" }}
              >
                <Table>
                  <TableBody>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={[10, 15, 25, 50]}
                        count={subscriberList?.rowCount || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        SelectProps={{
                          inputProps: { "aria-label": "rows per page" },
                          native: true,
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                      />
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </Box>
        <ConfirmationModal
          open={isStatusModalOpen}
          onClose={closeModal}
          heading="Change Status Confirmation"
          confirmationText="You're about to update the status of this record. Do you want to continue?"
          confirmationButtonText="Proceed"
          onConfirm={performStatusChange}
        />

        <ConfirmationModal
          open={isDeleteModalOpen}
          onClose={closeModal}
          heading="Delete Confirmation"
          confirmationText="This action will delete the selected webhook configuration. Are you sure you want to proceed?"
          confirmationButtonText="Delete"
          onConfirm={performDeletion}
        />
      </Box>
    </>
  );
};

export default ManageWebHooks;
